import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type FontSizeOption = 'medium' | 'large' | 'small';

@Injectable({
  providedIn: 'root'
})
export class FontSizeService {
  private fontSizeSubject = new BehaviorSubject<FontSizeOption>('medium');
  private fontSizeFactorMap: Record<FontSizeOption, number> = {
    medium: 1.0,
    large: 1.1,   // 10% larger
    small: 0.9    // 10% smaller
  };

  constructor() {
    // Initialize with default font size
    this.applyFontSize('medium');
  }

  // Get current font size option
  public get currentFontSize(): FontSizeOption {
    return this.fontSizeSubject.value;
  }

  // Get font size observable for components to subscribe to
  public get fontSize$(): Observable<FontSizeOption> {
    return this.fontSizeSubject.asObservable();
  }

  // Set font size option
  public setFontSize(size: FontSizeOption): void {
    if (this.currentFontSize !== size) {
      this.fontSizeSubject.next(size);
      this.applyFontSize(size);
    }
  }

  // Apply font size to the document using CSS variable
  private applyFontSize(size: FontSizeOption): void {
    // First add the class to the body for any class-based styles
    document.body.classList.remove('font-size-default', 'font-size-large', 'font-size-small');
    document.body.classList.add(`font-size-${size}`);

    // Then set the CSS variable for precise control
    document.documentElement.style.setProperty('--font-size-factor', this.fontSizeFactorMap[size].toString());

    // Force a reflow to ensure all components update
    this.forceReflow();
  }

  // Get the font size factor (multiplier)
  public getFontSizeFactor(): number {
    return this.fontSizeFactorMap[this.currentFontSize];
  }

  // Force a reflow to make sure CSS changes are applied
  private forceReflow(): void {
    // This will force the browser to reflow the document
    document.body.offsetHeight;

    // For Angular Material components, we need an additional step
    setTimeout(() => {
      // This will trigger Angular change detection and update Material components
      window.dispatchEvent(new Event('resize'));
    }, 0);
  }
}
